import { create } from 'zustand';
import { api, post } from '@/lib/api';

/**
 * The punter's session: a public account, apart from the advertiser's `session` store and from
 * its cookie. One browser may hold both, and neither screen reads the other's.
 */

export interface PunterPermissions {
  sign_in: boolean;
  save: boolean;
  share: boolean;
  max_lists: number;
  max_items: number;
}

export interface Punter {
  id: string;
  email: string;
  phone: string | null;
  email_verified: boolean;
  created_at: number;
  last_login_at: number | null;
  permissions: PunterPermissions;
}

type Scope = 'full' | 'verify';

interface PunterState {
  punter: Punter | null;
  scope: Scope | null;
  /** `error`: the server could not be asked, which is not the same as signed out. */
  status: 'idle' | 'loading' | 'ready' | 'error';
  load: () => Promise<void>;
  register: (body: { email: string; phone?: string; password: string; adult: boolean }) => Promise<void>;
  verify: (code: string) => Promise<void>;
  resend: () => Promise<void>;
  login: (identifier: string, password: string) => Promise<Scope>;
  logout: () => Promise<void>;
}

interface SessionAnswer {
  punter: Punter;
  scope: Scope;
}

export const usePunter = create<PunterState>((set) => ({
  punter: null,
  scope: null,
  status: 'idle',

  load: async () => {
    set({ status: 'loading' });
    try {
      const data = await api<{ punter: Punter | null; scope?: Scope }>('/punter/auth/me');
      set({ punter: data.punter, scope: data.scope ?? null, status: 'ready' });
    } catch {
      set({ status: 'error' });
    }
  },

  register: async (body) => {
    const data = await post<SessionAnswer>('/punter/auth/register', body);
    set({ punter: data.punter, scope: data.scope, status: 'ready' });
  },

  verify: async (code) => {
    const data = await post<SessionAnswer>('/punter/auth/verify', { code });
    set({ punter: data.punter, scope: data.scope, status: 'ready' });
  },

  resend: async () => {
    await post('/punter/auth/resend');
  },

  login: async (identifier, password) => {
    const data = await post<SessionAnswer>('/punter/auth/login', { identifier, password });
    set({ punter: data.punter, scope: data.scope, status: 'ready' });
    return data.scope;
  },

  logout: async () => {
    await post('/punter/auth/logout');
    set({ punter: null, scope: null });
  },
}));

/** True for a verified punter with a full session: somebody who can save. */
export const isSignedIn = (state: Pick<PunterState, 'punter' | 'scope'>) =>
  state.punter !== null && state.scope === 'full';
